import { useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Star,
  Footprints,
  MessageCircle,
  Newspaper,
} from "lucide-react";
import { Alignment, Fit, Layout, useRive } from "@rive-app/react-canvas";
import NextDinner from "../components/NextDinner";
import radarAnimation from "@/assets/rive/radar.riv";
import rainbowCloudAnimation from "@/assets/rive/rainbow_cloud.riv";
import martiniAnimation from "@/assets/rive/martini.riv";
import { Expandable } from "../components/ux/Expandable";
import { Chip } from "../components/ux/Chip";
import { SectionBackground } from "@/components/ux/SectionBackground";
import { HowItWorks } from "../components/HowItWorks";
import { useT } from "@/lib/i18n";
import jayImg from "@/assets/images/jay.webp";
import danielImg from "@/assets/images/daniel.webp";
import mayaImg from "@/assets/images/maya.webp";
import timeoutImg from "@/assets/images/timeout.svg";
import themImg from "@/assets/images/them.svg";
import tracking from "@/lib/tracking";
import { onboardingHref } from "@/lib/onboardingUrl";
import AppStoreBadges from "@/components/AppStoreBadges";
import { OnboardingDialog } from "@/components/OnboardingDialog";
import { SiteHeader } from "@/components/SiteHeader";
import { LandingFooter } from "@/components/LandingFooter";
import { Link } from "wouter";

export default function Landing() {
  const { t } = useT("landing");
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  const { RiveComponent: RadarRive } = useRive({
    src: radarAnimation,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.Center,
    }),
  });

  const { RiveComponent: RainbowRive } = useRive({
    src: rainbowCloudAnimation,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.Center,
    }),
  });

  const { RiveComponent: MartiniRive } = useRive({
    src: martiniAnimation,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.TopCenter,
    }),
  });

  const scrollToFeatures = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const featuresElement = document.getElementById("features");
    if (featuresElement) {
      featuresElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="relative overflow-hidden">
      <OnboardingDialog
        isOpen={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
      />
      <div className="z-10">
        <SiteHeader
          links={
            <>
              <Link
                href="/affiliate"
                className="text-type-950 dark:text-neutral-100 hover:text-primary-500 transition-colors font-medium"
              >
                {t("nav.affiliate")}
              </Link>
              <Link
                href="/locations"
                className="text-type-950 dark:text-neutral-100 hover:text-primary-500 transition-colors font-medium"
              >
                {t("nav.locations")}
              </Link>
            </>
          }
          login={
            <a
              href="https://app.outclose.com/"
              className="btn-primary px-4 py-2 w-auto"
            >
              {t("app:common.buttons.login")}
            </a>
          }
        />
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center flex flex-col items-center justify-center"
          >
            <div className="min-h-screen flex flex-col justify-end items-center">
              <div className="flex-[0_1_100px]" />
              {/* Logo */}
              <div className="glass-bar my-8">{t("hero.tagline")}</div>

              <h1 className="text-5xl md:text-7xl font-semibold mb-6 text-type-950 dark:text-neutral-50 font-Poppins ">
                {t("hero.title")}
                <br />
                {t("hero.titleLine2")}
              </h1>

              <p className="text-xl md:text-xl font-Poppins mb-4 max-w-2xl mx-auto dark:text-neutral-100">
                {t("hero.subtitle")}
                <br />
                {t("hero.subtitleLine2")}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center my-8">
                <a
                  href="#features"
                  onClick={scrollToFeatures}
                  className="w-full sm:w-auto"
                >
                  <button className="btn glass-btn-secondary px-8 py-2 text-lg w-full">
                    {t("app:common.buttons.learnMore")}
                  </button>
                </a>
                <a
                  className="btn glass-btn-primary px-8 py-2 text-lg w-full sm:w-auto"
                  href="https://app.outclose.com/"
                  target="_self"
                >
                  {t("hero.cta")}
                  <Sparkles className="inline-block ml-2 w-5 h-5" />
                </a>
              </div>

              {/* Next Dinner Countdown */}
              <NextDinner />

              {/* App Store Badges */}
              <AppStoreBadges
                className="justify-center"
                badgeHeight={40}
                source="landing_hero"
              />
              <div className="md:flex-[0_1_200px] flex-[0_1_100px] min-h-16" />
            </div>

            {/* Features */}
            <div id="features" className="flex justify-center items-center p-4">
              <Chip
                title={t("features.chipTitle")}
                variant="primary"
                icon={<Star strokeWidth={2} />}
              />
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                  rotateY: 0,
                  transformPerspective: 1200,
                }}
                animate={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="card text-center"
              >
                <div className="w-full h-[200px] rounded-xl overflow-hidden">
                  <RadarRive className="w-full h-full fit-cover" />
                </div>
                <h3 className="text-xl font-semibold my-4 dark:text-neutral-100">
                  {t("features.intelligentMatching.title")}
                </h3>
                <p className="text-dark-muted dark:text-neutral-400">
                  {t("features.intelligentMatching.description")}
                </p>
              </motion.div>

              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                  rotateY: 0,
                  transformPerspective: 1200,
                }}
                animate={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="card text-center "
              >
                <div className="w-full h-[200px] rounded-xl overflow-hidden">
                  <RainbowRive />
                </div>
                <h3 className="text-xl font-semibold my-4 dark:text-neutral-100">
                  {t("features.builtForAll.title")}
                </h3>
                <p className="text-dark-muted dark:text-neutral-400">
                  {t("features.builtForAll.description")}
                </p>
              </motion.div>

              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                  rotateY: 0,
                  transformPerspective: 1200,
                }}
                animate={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="card text-center rotate-x-[-20deg]"
              >
                <div className="w-full h-[200px] rounded-xl overflow-hidden">
                  <MartiniRive />
                </div>
                <h3 className="text-xl font-semibold my-4 dark:text-neutral-100">
                  {t("features.noHassle.title")}
                </h3>
                <p className="text-dark-muted dark:text-neutral-400">
                  {t("features.noHassle.description")}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* How it works */}
        <SectionBackground className="text-center py-16 justify-center items-center">
          <Chip
            title={t("howItWorks.chipTitle")}
            variant="primary"
            icon={<Footprints strokeWidth={2} />}
          />
          <HowItWorks />
        </SectionBackground>

        {/* Interactive Question */}
        <div className="px-4 sm:px-6 py-16 bg-white dark:bg-neutral-900">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-8 text-type-950 dark:text-neutral-50">
              {t("quiz.question")}
            </h2>
            <div className="flex flex-row gap-4 justify-center items-stretch">
              <a
                href={onboardingHref()}
                className="flex-1 sm:flex-none"
                onClick={() => tracking.landingHeadFirst()}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="card max-w-40 max-h-40 w-full h-full hover:shadow-xl hover:border-primary-500 transition-all cursor-pointer group"
                >
                  <div className="text-5xl mb-3">🧠</div>
                  <div className="text-xl font-semibold text-type-950 dark:text-neutral-100 group-hover:text-primary-500 transition-colors">
                    {t("quiz.headFirst")}
                  </div>
                </motion.button>
              </a>
              <a
                href={onboardingHref()}
                className="flex-1 sm:flex-none"
                onClick={() => tracking.landingHeartFirst()}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="card max-w-40 max-h-40 w-full h-full hover:shadow-xl hover:border-primary-500 transition-all cursor-pointer group"
                >
                  <div className="text-5xl mb-3">❤️</div>
                  <div className="text-xl font-semibold text-type-950 dark:text-neutral-100 group-hover:text-primary-500 transition-colors">
                    {t("quiz.heartFirst")}
                  </div>
                </motion.button>
              </a>
            </div>
            <a
              href={onboardingHref()}
              className="btn-primary inline-block p-4 px-8 mt-6"
            >
              {t("quiz.cta")}
            </a>
          </motion.div>
        </div>
        <div className="bg-white dark:bg-neutral-900 text-center px-4 sm:px-6 py-16 lg:px-8 justify-center items-center">
          <Chip
            title={t("press.chipTitle")}
            variant="primary"
            icon={<Newspaper strokeWidth={2} />}
          />
          <p className="text-center p-8">
            <a
              href="https://www.timeout.com/newyork/news/this-new-site-sets-lgbtq-new-yorkers-up-on-friend-datesso-i-sat-down-for-dinner-with-strangers-111225"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={timeoutImg}
                alt="Timeout"
                className="dark:invert m-4"
                style={{ display: "inline-block", height: 54 }}
              />
            </a>
            <a
              href="https://www.them.us/story/outclose-gayborhood-app-queer-dinners-finding-friends"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={themImg}
                alt="Them"
                className="dark:invert m-4"
                style={{ display: "inline-block", height: 54 }}
              />
            </a>
          </p>
        </div>

        {/* Feedback */}
        <SectionBackground className="text-center px-4 sm:px-6 py-16 lg:px-8 justify-center items-center">
          <Chip
            title={t("testimonials.chipTitle")}
            variant="primary"
            icon={<MessageCircle strokeWidth={2} />}
          />
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="card p-6 text-left flex flex-col"
            >
              <p className="text-dark-muted dark:text-neutral-400 mb-4 italic flex-1">
                "I'd only been in the city a couple months and didn't really
                know anyone yet. I almost didn't go but figured worst case I'd
                eat dinner and leave. Ended up staying way longer than I planned
                because it felt easy, not like networking or anything."
              </p>
              <p className="font-semibold text-type-950 dark:text-neutral-100">
                Evan (he/him), 32, Chelsea NYC
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="card p-6 text-left flex flex-col"
            >
              <p className="text-dark-muted dark:text-neutral-400 mb-4 italic flex-1">
                "Work's been nonstop so I haven't had the energy to make new
                friends. Having it all set up for me was kind of a relief. I
                just walked in, sat down, and the rest happened on its own
                which… honestly I didn't expect."
              </p>
              <p className="font-semibold text-type-950 dark:text-neutral-100">
                Luis (he/him), 41, Hell's Kitchen NYC
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="card p-6 text-left flex flex-col"
            >
              <p className="text-dark-muted dark:text-neutral-400 mb-4 italic flex-1">
                "Big queer spaces sometimes make me anxious, I get quiet and
                kind of disappear in the corner. Here it felt calmer, like
                everyone actually had space. I didn't have to push myself to
                talk, it just came out normal."
              </p>
              <p className="font-semibold text-type-950 dark:text-neutral-100">
                Marcus (they/them), 29, Brooklyn NYC
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="card p-6 text-left flex flex-col"
            >
              <p className="text-dark-muted dark:text-neutral-400 mb-4 italic flex-1">
                "I wasn't sure about the idea of dinner with strangers, sounded
                weird in my head. But I laughed more than I have in a while and
                we ended up grabbing another drink after. It felt really human,
                which is rare these days."
              </p>
              <p className="font-semibold text-type-950 dark:text-neutral-100">
                Tasha (she/her), 34, Lower East Side NYC
              </p>
            </motion.div>
          </div>
        </SectionBackground>

        {/* Meet Your Future Table */}
        <div className="py-8 bg-white dark:bg-neutral-900">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-5xl mx-auto"
          >
            <div className="px-4">
              <h2 className="text-3xl md:text-4xl font-semibold mb-2 text-type-950 dark:text-neutral-50">
                {t("meetTable.title")}
              </h2>
              <p className="text-lg text-dark-muted dark:text-neutral-400 mb-4">
                {t("meetTable.subtitle")}
              </p>
            </div>

            <div className="overflow-x-scroll scrollbar-hidden w-full max-w-7xl mx-auto">
              <div className="flex flex-row gap-8 p-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="card p-0 overflow-hidden min-w-64"
                >
                  <div className="w-full h-64 overflow-hidden">
                    <img
                      src={jayImg}
                      alt="Jay"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-left">
                    <h3 className="text-2xl font-semibold text-type-950 dark:text-neutral-100 mb-2">
                      JAY, 33
                    </h3>
                    <p className="text-dark-muted dark:text-neutral-400">
                      Product designer, big on astrology debates, Virgo
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="card p-0 overflow-hidden min-w-64"
                >
                  <div className="w-full h-64 overflow-hidden">
                    <img
                      src={danielImg}
                      alt="Daniel"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-left">
                    <h3 className="text-2xl font-semibold text-type-950 dark:text-neutral-100 mb-2">
                      DANIEL, 38
                    </h3>
                    <p className="text-dark-muted dark:text-neutral-400">
                      Architect, secretly into karaoke, Scorpio
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="card p-0 overflow-hidden min-w-64"
                >
                  <div className="w-full h-64 overflow-hidden">
                    <img
                      src={mayaImg}
                      alt="Maya"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-left">
                    <h3 className="text-2xl font-semibold text-type-950 dark:text-neutral-100 mb-2">
                      MAYA, 41
                    </h3>
                    <p className="text-dark-muted dark:text-neutral-400">
                      Creative strategist, brunches with her exes, Leo
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* FAQ */}
        <SectionBackground className="text-center px-4 sm:px-6 pt-16 lg:px-8 pb-16">
          <h2 className="text-3xl md:text-4xl font-semibold mb-8 text-type-950 dark:text-neutral-50 font-Poppins">
            {t("faq.chipTitle")}
          </h2>
          <div className="flex flex-col gap-4 max-w-xl mx-auto">
            <Expandable title={t("faq.whatsIncluded.question")}>
              {t("faq.whatsIncluded.answer")}
            </Expandable>
            <Expandable title={t("faq.howAssigned.question")}>
              {t("faq.howAssigned.answer")}
            </Expandable>
            <Expandable title={t("faq.whoCanJoin.question")}>
              {t("faq.whoCanJoin.answer")}
            </Expandable>
            <Expandable title={t("faq.isDating.question")}>
              {t("faq.isDating.answer")}
            </Expandable>
            <Expandable title={t("faq.bringPartner.question")}>
              {t("faq.bringPartner.answer")}
            </Expandable>
            <Expandable title={t("faq.cost.question")}>
              {t("faq.cost.answer")}
            </Expandable>
            <Expandable title={t("faq.whoBehind.question")}>
              {t("faq.whoBehind.answer")}
            </Expandable>
          </div>
        </SectionBackground>

        {/* Footer */}
        <LandingFooter />
      </div>
    </div>
  );
}
